import React, { useEffect, useState, useRef } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { fetchGemPacks, startCheckout, getCheckoutStatus } from "../lib/store";
import { fetchOrCreateProfile, getStoredName } from "../lib/api";
import { sfx } from "../lib/sound";
import { isNative } from "../lib/native";
import { buyPlayProduct, whenPlayBillingReady } from "../lib/playBilling";
import { mountBanner, unmountBanner, shouldShowBanner, BANNER_HEIGHT_PX, markAdsRemovedUntil, isAdsRemovedActive } from "../lib/banner";
import { track } from "../lib/analytics";

const SKIN_LABEL = {
  gold_card_back: "Gold Card-Back skin",
  neon_holo: "Neon Holo skin",
};

const POLL_MS = 2000;
const MAX_POLLS = 6; // ~12s window

export default function Store() {
  const nav = useNavigate();
  const [packs, setPacks] = useState([]);
  const [packsLoading, setPacksLoading] = useState(true);
  const [packsError, setPacksError] = useState("");
  const [firstPurchaseEligible, setFirstPurchaseEligible] = useState(false);
  const [profile, setProfile] = useState(null);
  const [busy, setBusy] = useState(false);
  const [busyPackId, setBusyPackId] = useState(null);
  const [error, setError] = useState("");
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const canceled = params.get("canceled");

  const [pollMsg, setPollMsg] = useState(null); // {kind, text, gems, chips}
  const pollAttempts = useRef(0);
  const adFreeCardRef = useRef(null);
  const highlight = params.get("highlight");
  const [pulseAdFree, setPulseAdFree] = useState(false);

  useEffect(() => {
    (async () => {
      try { setProfile(await fetchOrCreateProfile(getStoredName())); } catch (e) { console.error(e); }
      try {
        setPacksLoading(true);
        setPacksError("");
        const r = await fetchGemPacks();
        setPacks(r.packs);
        setFirstPurchaseEligible(r.firstPurchaseEligible);
      } catch (e) {
        console.error(e);
        setPacksError(e?.message || "Couldn't load gem packs. Pull-to-refresh or try again.");
      } finally {
        setPacksLoading(false);
      }
    })();
  }, []);

  // Deep-link from the banner "Remove ads →" link: scroll the ad-free pack
  // into view and flash a short pulse so the upsell is instantly obvious.
  useEffect(() => {
    if (highlight !== "ad_free" || !packs.length) return;
    try { track("remove_ads_link_clicked", { source: "banner" }); } catch {}
    const t = setTimeout(() => {
      adFreeCardRef.current?.scrollIntoView({ behavior: "smooth", block: "center" });
      setPulseAdFree(true);
      setTimeout(() => setPulseAdFree(false), 2400);
    }, 250);
    return () => clearTimeout(t);
  }, [highlight, packs.length]);

  // Poll for payment status if returning from Stripe
  useEffect(() => {
    if (!sessionId) return;
    let stop = false;
    setPollMsg({ kind: "pending", text: "Confirming your purchase…" });
    const poll = async () => {
      if (stop) return;
      pollAttempts.current += 1;
      try {
        const s = await getCheckoutStatus(sessionId);
        if (s.payment_status === "paid" && s.credited) {
          setPollMsg({
            kind: "ok",
            text: "Purchase confirmed!",
            gems: s.gems,
            chips: s.bonus_chips,
          });
          sfx.win();
          // Refresh profile + first-purchase flag (now used)
          try {
            const fresh = await fetchOrCreateProfile(getStoredName());
            setProfile(fresh);
            if (fresh?.ads_removed_until) markAdsRemovedUntil(fresh.ads_removed_until);
          } catch {}
          try {
            const r = await fetchGemPacks();
            setPacks(r.packs);
            setFirstPurchaseEligible(r.firstPurchaseEligible);
          } catch {}
          // Strip session_id from URL
          window.history.replaceState({}, document.title, "/store");
          return;
        }
        if (s.status === "expired") {
          setPollMsg({ kind: "err", text: "Checkout expired. Please try again." });
          return;
        }
        if (pollAttempts.current >= MAX_POLLS) {
          setPollMsg({ kind: "err", text: "Still processing. Check back in a minute." });
          return;
        }
        setTimeout(poll, POLL_MS);
      } catch (e) {
        setPollMsg({ kind: "err", text: e?.response?.data?.detail || "Status check failed." });
      }
    };
    poll();
    return () => { stop = true; };
  }, [sessionId]);

  useEffect(() => {
    if (canceled === "1") {
      setPollMsg({ kind: "info", text: "Checkout canceled. No charge made." });
    }
  }, [canceled]);

  // Banner ad — passive screen, mount on enter, unmount on leave OR when
  // the user has an ACTIVE ad-free pass. Re-runs when ads_removed_until flips.
  useEffect(() => {
    if (isAdsRemovedActive(profile)) {
      unmountBanner();
      return undefined;
    }
    if (!shouldShowBanner()) return undefined;
    mountBanner();
    return () => { unmountBanner(); };
  }, [profile?.ads_removed_until]);

  const buy = async (pack_id) => {
    setBusy(true); setBusyPackId(pack_id); setError("");
    try {
      sfx.click();
      if (isNative()) {
        // Android — use Google Play Billing. Map our web pack_id to the Play SKU.
        const playSku = { starter: "gems_starter", stack: "gems_stack", high_roller: "gems_high_roller", ad_free: "ad_free" }[pack_id];
        try {
          await whenPlayBillingReady();
        } catch (e) {
          throw new Error("In-app purchases aren't activated yet. The developer is setting up Play Console products — try again in a few hours!");
        }
        try {
          await buyPlayProduct(playSku);
        } catch (e) {
          if (e?.message === "PRODUCT_NOT_READY") {
            throw new Error("In-app purchases aren't fully set up yet. The developer is finishing Play Console configuration — try again in a few hours!");
          }
          throw e;
        }
        // Refresh profile after purchase (backend credits gems via /api/play-billing/verify)
        setTimeout(async () => {
          try { setProfile(await fetchOrCreateProfile(getStoredName())); } catch {}
          setPollMsg({ kind: "ok", text: "Purchase confirmed!" });
          setBusy(false); setBusyPackId(null);
        }, 1500);
      } else {
        // Web — Stripe Checkout
        const { url } = await startCheckout(pack_id);
        window.location.href = url;
      }
    } catch (e) {
      setError(e?.response?.data?.detail || e?.message || "Failed to start checkout.");
      setBusy(false); setBusyPackId(null);
    }
  };

  return (
    <div
      className="min-h-screen w-full px-4 md:px-8 py-8"
      style={{ paddingBottom: `${BANNER_HEIGHT_PX + 24}px` }}
    >
      <div className="max-w-5xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between gap-2 mb-6 md:mb-8 flex-wrap">
          <div className="flex items-center gap-2 md:gap-3">
            <button className="btn-ghost" onClick={() => nav("/")} data-testid="store-back-btn">Back</button>
            <h1 className="font-display font-black text-xl md:text-3xl tracking-tight">Gem Store</h1>
          </div>
          {profile && (
            <div className="flex items-center gap-1.5 md:gap-2">
              <div className="glass rounded-full px-2.5 py-1.5 md:px-3 md:py-2 flex items-center gap-1.5" data-testid="store-chips" style={{ minHeight: 32 }}>
                <span style={{ color: "#FFD700", fontSize: 13 }}>◆</span>
                <span className="font-mono font-bold" style={{ fontSize: 12 }}>{(profile.chips || 0).toLocaleString()}</span>
              </div>
              <div
                className="glass rounded-full px-2.5 py-1.5 md:px-3 md:py-2 flex items-center gap-1.5"
                style={{ border: "1px solid rgba(0,229,255,0.45)", minHeight: 32 }}
                data-testid="store-gems"
              >
                <span style={{ color: "#00E5FF", fontSize: 13 }}>◈</span>
                <span className="font-mono font-bold" style={{ fontSize: 12 }}>{(profile.gems || 0).toLocaleString()}</span>
              </div>
            </div>
          )}
        </div>

        {/* Status banner (post-Stripe redirect) */}
        {pollMsg && (
          <motion.div
            initial={{ y: -8, opacity: 0 }} animate={{ y: 0, opacity: 1 }}
            className="rounded-2xl px-4 py-3 mb-6 flex items-center gap-3"
            style={{
              background: pollMsg.kind === "ok" ? "rgba(61,220,151,0.10)" :
                          pollMsg.kind === "err" ? "rgba(255,92,92,0.10)" :
                          "rgba(255,215,0,0.10)",
              border: `1px solid ${pollMsg.kind === "ok" ? "rgba(61,220,151,0.4)" :
                                   pollMsg.kind === "err" ? "rgba(255,92,92,0.4)" :
                                   "rgba(255,215,0,0.35)"}`,
            }}
            data-testid={`store-${pollMsg.kind}-banner`}
          >
            <span style={{ fontSize: 18 }}>
              {pollMsg.kind === "ok" ? "✓" : pollMsg.kind === "err" ? "⚠" : "⌛"}
            </span>
            <div className="flex-1">
              <div className="font-display font-bold text-sm">{pollMsg.text}</div>
              {pollMsg.gems != null && (
                <div className="text-xs text-white/70 mt-0.5">
                  +{pollMsg.gems.toLocaleString()} gems · +{(pollMsg.chips || 0).toLocaleString()} chips
                </div>
              )}
            </div>
          </motion.div>
        )}
        {error && (
          <div className="rounded-2xl px-4 py-3 mb-6 text-sm" style={{ color: "#FF8A8A", border: "1px solid rgba(255,92,92,0.4)", background: "rgba(255,92,92,0.06)" }}>
            {error}
          </div>
        )}

        {/* Description */}
        <p className="text-white/65 text-sm mb-6 max-w-xl">
          Gems are the premium currency — earn cosmetic skins and bonus chips
          for tournament entries. {isNative()
            ? "Pay securely with your Google Play account."
            : "Buy with a card, instant credit."}
        </p>

        {/* Pack grid — gem packs only */}
        {packsLoading ? (
          <div className="grid md:grid-cols-3 gap-4" data-testid="store-packs-loading">
            {[0,1,2].map((i) => (
              <div
                key={`pack-skel-${i}`}
                className="glass rounded-3xl p-5"
                style={{
                  minHeight: 220,
                  background: "linear-gradient(110deg, rgba(255,255,255,0.04) 30%, rgba(255,255,255,0.08) 50%, rgba(255,255,255,0.04) 70%)",
                  backgroundSize: "200% 100%",
                  animation: "shimmer 1.6s ease-in-out infinite",
                  border: "1px solid rgba(255,255,255,0.06)",
                }}
              />
            ))}
          </div>
        ) : packsError ? (
          <div
            className="glass rounded-3xl p-5 text-center"
            style={{ border: "1px solid rgba(255,92,92,0.35)" }}
            data-testid="store-packs-error"
          >
            <div className="text-white/80 text-sm mb-3">{packsError}</div>
            <button
              className="btn-ghost"
              onClick={() => window.location.reload()}
              data-testid="store-packs-retry-btn"
            >Retry</button>
          </div>
        ) : packs.length === 0 ? (
          <div
            className="glass rounded-3xl p-5 text-center text-white/65 text-sm"
            data-testid="store-packs-empty"
          >No gem packs available yet. Check back soon.</div>
        ) : (
        <div className="grid md:grid-cols-3 gap-4" data-testid="store-pack-grid">
          {packs.filter((p) => p.id !== "ad_free").map((pack) => {
            const popular = pack.id === "stack";
            return (
              <motion.div
                key={pack.id}
                whileHover={{ y: -4 }}
                className="glass rounded-3xl p-5 flex flex-col"
                style={{
                  border: popular ? "1.5px solid rgba(0,229,255,0.55)" :
                                    "1px solid rgba(255,255,255,0.10)",
                  boxShadow: popular ? "0 0 32px rgba(0,229,255,0.18)" : undefined,
                }}
                data-testid={`pack-${pack.id}`}
              >
                {popular && (
                  <span
                    className="self-start label-eyebrow rounded-full px-2 py-0.5 mb-2"
                    style={{
                      background: "rgba(0,229,255,0.12)",
                      color: "#00E5FF",
                      fontSize: 9,
                    }}
                  >MOST POPULAR</span>
                )}
                {firstPurchaseEligible && (
                  <span
                    className="self-start label-eyebrow rounded-full px-2 py-0.5 mb-2"
                    style={{
                      background: "linear-gradient(135deg,#FFD700,#FF6B6B)",
                      color: "#0B0B0B",
                      fontSize: 9,
                      letterSpacing: 1,
                      boxShadow: "0 0 14px rgba(255,215,0,0.45)",
                    }}
                    data-testid={`first-purchase-badge-${pack.id}`}
                  >FIRST TIME · 2× GEMS</span>
                )}
                <h3 className="font-display font-black text-xl">{pack.name}</h3>
                <div className="text-white/55 text-xs mb-4">{pack.tagline}</div>

                <div className="flex items-baseline gap-1 mb-3">
                  <span style={{ color: "#00E5FF", fontSize: 22 }}>◈</span>
                  <span className="font-display font-black text-3xl">
                    {(firstPurchaseEligible ? pack.gems * 2 : pack.gems).toLocaleString()}
                  </span>
                  {firstPurchaseEligible && (
                    <span
                      className="line-through text-white/40 text-base ml-1"
                      data-testid={`pack-${pack.id}-original-gems`}
                    >{pack.gems.toLocaleString()}</span>
                  )}
                  <span className="text-white/55 text-xs ml-1">gems</span>
                </div>

                <ul className="text-sm text-white/80 space-y-1 mb-5">
                  <li>+ <b style={{ color: "#FFD700" }}>{pack.bonus_chips.toLocaleString()}</b> chips bonus</li>
                  {pack.skin && <li>+ <b>{SKIN_LABEL[pack.skin] || pack.skin}</b></li>}
                </ul>

                <div className="mt-auto flex items-center justify-between gap-3">
                  <span className="font-mono font-bold text-lg">${pack.amount_usd.toFixed(2)}</span>
                  <button
                    disabled={busy}
                    onClick={() => buy(pack.id)}
                    className="rounded-full px-4 py-2 font-display font-bold text-xs"
                    style={{
                      background: popular ? "#00E5FF" : "#FFD700",
                      color: "#0B0C10",
                      opacity: busy && busyPackId !== pack.id ? 0.5 : (busy ? 0.85 : 1),
                    }}
                    data-testid={`buy-${pack.id}-btn`}
                  >
                    {busyPackId === pack.id ? "Loading…" : "BUY NOW"}
                  </button>
                </div>
              </motion.div>
            );
          })}
        </div>
        )}
        {/* Ad-Free Pass — premium hero tile. Renders differently based on
            whether the user has an active pass, an expired one, or none. */}
        {(() => {
          const adFree = packs.find((p) => p.id === "ad_free");
          if (!adFree) return null;
          const until = profile?.ads_removed_until ? new Date(profile.ads_removed_until) : null;
          const active = until && !Number.isNaN(until.getTime()) && until.getTime() > Date.now();
          const daysLeft = active ? Math.ceil((until.getTime() - Date.now()) / 86400000) : 0;
          const fmtDate = active ? until.toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" }) : null;

          if (active) {
            return (
              <div
                className="glass mt-6 rounded-3xl px-5 py-5 flex flex-col sm:flex-row sm:items-center gap-4"
                style={{
                  border: "1.5px solid rgba(61,220,151,0.45)",
                  background: "linear-gradient(135deg, rgba(61,220,151,0.10), rgba(0,229,255,0.04))",
                }}
                data-testid="ad-free-owned"
              >
                <div
                  className="rounded-full flex items-center justify-center text-2xl shrink-0 mx-auto sm:mx-0"
                  style={{
                    width: 48, height: 48,
                    background: "rgba(61,220,151,0.20)",
                    border: "1.5px solid rgba(61,220,151,0.55)",
                  }}
                >🛡️</div>
                <div className="flex-1 min-w-0 text-center sm:text-left">
                  <div
                    className="label-eyebrow mb-0.5"
                    style={{ color: "#3DDC97", letterSpacing: 2 }}
                    data-testid="ad-free-active-label"
                  >AD-FREE PASS · ACTIVE</div>
                  <div className="font-display font-bold">
                    {daysLeft} {daysLeft === 1 ? "day" : "days"} remaining
                  </div>
                  <div className="text-white/55 text-xs mt-0.5">Renews manually · expires {fmtDate}</div>
                </div>
                <button
                  disabled={busy}
                  onClick={() => buy(adFree.id)}
                  className="rounded-full px-5 py-2 font-display font-bold text-xs shrink-0"
                  style={{
                    background: "linear-gradient(135deg, #FFD700, #FF6B6B)",
                    color: "#0B0B0B",
                    boxShadow: busy ? "none" : "0 0 16px rgba(255,215,0,0.4)",
                    opacity: busy ? 0.6 : 1,
                  }}
                  data-testid="extend-ad_free-btn"
                >+30 DAYS · ${adFree.amount_usd.toFixed(2)}</button>
              </div>
            );
          }
          return (
            <motion.div
              ref={adFreeCardRef}
              whileHover={{ y: -4 }}
              animate={pulseAdFree
                ? { boxShadow: [
                    "0 0 38px rgba(255,107,107,0.18)",
                    "0 0 72px rgba(255,107,107,0.8)",
                    "0 0 38px rgba(255,107,107,0.18)",
                  ], scale: [1, 1.015, 1] }
                : {}}
              transition={pulseAdFree ? { duration: 1.2, repeat: 1 } : {}}
              className="glass mt-6 rounded-3xl p-6 flex flex-col md:flex-row items-start md:items-center gap-6 relative overflow-hidden"
              style={{
                border: "1.5px solid rgba(255,107,107,0.55)",
                boxShadow: "0 0 38px rgba(255,107,107,0.18)",
                background:
                  "linear-gradient(135deg, rgba(255,107,107,0.08), rgba(255,140,0,0.04))",
              }}
              data-testid="pack-ad_free"
            >
              <div
                className="rounded-3xl flex items-center justify-center text-3xl shrink-0"
                style={{
                  width: 72, height: 72,
                  background: "linear-gradient(135deg, #FF6B6B, #FFD700)",
                  boxShadow: "0 0 24px rgba(255,107,107,0.5)",
                }}
              >🛡️</div>

              <div className="flex-1 min-w-0">
                <div
                  className="label-eyebrow mb-1"
                  style={{ color: "#FF8A8A", letterSpacing: 2 }}
                >FAN FAVOURITE</div>
                <h3 className="font-display font-black text-2xl">{adFree.name}</h3>
                <div className="text-white/65 text-sm mt-1 mb-3">
                  {adFree.tagline}. Plus <b style={{ color: "#00E5FF" }}>{adFree.gems}</b> gems
                  {firstPurchaseEligible ? " (×2 first-time = 400)" : ""} as a bonus.
                </div>
                <ul className="text-xs text-white/75 space-y-1 mb-2">
                  <li>✓ No banner ads for 30 days</li>
                  <li>✓ No interstitial ads between games</li>
                  <li>✓ Rewarded ads still available (your choice)</li>
                  <li>✓ Renews manually — no auto-charge</li>
                </ul>
              </div>

              <div className="flex flex-col items-end gap-2 shrink-0">
                <div className="text-right">
                  <span className="font-mono font-bold text-2xl">${adFree.amount_usd.toFixed(2)}</span>
                  <span className="text-white/45 text-xs ml-1">/ 30 days</span>
                </div>
                <button
                  disabled={busy}
                  onClick={() => buy(adFree.id)}
                  className="rounded-full px-6 py-2.5 font-display font-bold text-sm transition-all"
                  style={{
                    background: "linear-gradient(135deg, #FF6B6B, #FFD700)",
                    color: "#0B0C10",
                    boxShadow: busy ? "none" : "0 0 22px rgba(255,107,107,0.45)",
                    opacity: busy && busyPackId !== adFree.id ? 0.5 : (busy ? 0.85 : 1),
                  }}
                  data-testid="buy-ad_free-btn"
                >{busyPackId === adFree.id ? "Loading…" : "GET PASS"}</button>
              </div>
            </motion.div>
          );
        })()}

        <p className="text-[11px] text-white/40 text-center mt-8">
          {isNative()
            ? "Payments handled by Google Play. Test mode — no real charge."
            : "Payments processed securely by Stripe. Test mode — no real charge."}
        </p>
      </div>
    </div>
  );
}
